<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  onClose: {
    type: Function,
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();

const selectedFile = ref(null);
const isDragging = ref(false);
const isUploading = ref(false);
const uploadResults = ref(null);
const fileInputRef = ref(null);

const hasFile = computed(() => selectedFile.value !== null);
const hasResults = computed(() => uploadResults.value !== null);

const defaultResults = {
  success: 0,
  created: 0,
  updated: 0,
  skipped: [],
  errors: [],
  processed: 0,
  status: 'no_changes',
  message: '',
};

const normalizeResults = raw => {
  const merged = {
    ...defaultResults,
    ...(raw || {}),
  };

  merged.errors = Array.isArray(merged.errors) ? merged.errors : [];
  merged.skipped = Array.isArray(merged.skipped) ? merged.skipped : [];

  merged.success = Number(merged.success) || 0;
  merged.created = Number(merged.created) || 0;
  merged.updated = Number(merged.updated) || 0;
  merged.processed = Number(merged.processed) || 0;

  merged.status = merged.status || 'no_changes';
  merged.message = merged.message || '';

  return merged;
};

const buildSummaryMessage = result => {
  if (!result) {
    return '';
  }

  if (result.message) {
    return result.message;
  }

  const statusKey = result.status || 'no_changes';
  return t(`CANNED_MGMT.IMPORT.SUMMARY.STATUS.${statusKey}`, {
    count: result.success || 0,
    errorCount: result.errors?.length || 0,
  });
};

const summaryClasses = computed(() => {
  const status = uploadResults.value?.status;

  switch (status) {
    case 'success':
      return {
        container: 'bg-n-teal-2 border border-n-teal-6',
        heading: 'text-n-teal-11',
        body: 'text-n-teal-11',
      };
    case 'partial_success':
      return {
        container: 'bg-n-amber-2 border border-n-amber-6',
        heading: 'text-n-amber-11',
        body: 'text-n-amber-11',
      };
    case 'failed':
      return {
        container: 'bg-n-ruby-2 border border-n-ruby-6',
        heading: 'text-n-ruby-11',
        body: 'text-n-ruby-11',
      };
    default:
      return {
        container: 'bg-n-slate-2 border border-n-weak',
        heading: 'text-n-slate-12',
        body: 'text-n-slate-11',
      };
  }
});

const summaryMessage = computed(() => buildSummaryMessage(uploadResults.value));

const handleFileSelect = (event) => {
  const file = event.target.files[0];
  if (file) {
    validateAndSetFile(file);
  }
};

const validateAndSetFile = (file) => {
  // Validate file type
  if (!file.name.endsWith('.csv')) {
    useAlert(t('CANNED_MGMT.IMPORT.ALERT.INVALID_FILE_TYPE'));
    return;
  }

  // Validate file size (max 5MB)
  if (file.size > 5 * 1024 * 1024) {
    useAlert(t('CANNED_MGMT.IMPORT.ALERT.FILE_TOO_LARGE'));
    return;
  }

  selectedFile.value = file;
  uploadResults.value = null;
};

const handleDrop = (event) => {
  event.preventDefault();
  isDragging.value = false;

  const file = event.dataTransfer.files[0];
  if (file) {
    validateAndSetFile(file);
  }
};

const handleDragOver = (event) => {
  event.preventDefault();
  isDragging.value = true;
};

const handleDragLeave = () => {
  isDragging.value = false;
};

const openFilePicker = () => {
  fileInputRef.value?.click();
};

const removeFile = () => {
  selectedFile.value = null;
  uploadResults.value = null;
  if (fileInputRef.value) {
    fileInputRef.value.value = '';
  }
};

const handleImport = async () => {
  if (!selectedFile.value) return;

  isUploading.value = true;
  uploadResults.value = null;

  try {
    const rawResults = await store.dispatch('importCannedResponses', selectedFile.value);
    const normalizedResults = normalizeResults(rawResults);

    uploadResults.value = normalizedResults;

    const message = buildSummaryMessage(normalizedResults);
    if (message) {
      useAlert(message);
    }
  } catch (error) {
    useAlert(error.message || t('CANNED_MGMT.IMPORT.ALERT.FAILURE'));
  } finally {
    isUploading.value = false;
  }
};

const downloadTemplate = () => {
  const csvContent = `short_code,content_en,category,problem_tag,action_tag,ai_instructions,fr,it,es,nl,fi,da,sv,no
greeting,"Hello {{name}}!",general,,welcome,"Keep reply short and friendly.","Bonjour {{name}}!","Ciao {{name}}!","¡Hola {{name}}!","Hallo {{name}}!","Hei {{name}}!","Hej {{name}}!","Hej {{name}}!","Hei {{name}}!"
closing,"Thank you for contacting us!",general,,close,"Offer further help if needed.","Merci de nous avoir contactés!","Grazie per averci contattato!","¡Gracias por contactarnos!","Bedankt voor het contact!","Kiitos yhteydenotostasi!","Tak for din henvendelse!","Tack för att du kontaktar oss!","Takk for at du kontaktet oss!"`;

  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'canned_responses_template.csv');
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const closeModal = () => {
  if (!isUploading.value) {
    props.onClose();
  }
};
</script>

<template>
  <div class="flex flex-col h-auto max-w-2xl">
    <woot-modal-header
      :header-title="t('CANNED_MGMT.IMPORT.TITLE')"
      :header-content="t('CANNED_MGMT.IMPORT.DESCRIPTION')"
    />

    <div class="p-6 space-y-4">
      <!-- Instructions -->
      <div class="bg-n-slate-2 border border-n-weak rounded-lg p-4 space-y-2">
        <h3 class="font-semibold text-n-slate-12 flex items-center gap-2">
          <Icon icon="i-lucide-info" class="size-4" />
          {{ t('CANNED_MGMT.IMPORT.INSTRUCTIONS.TITLE') }}
        </h3>
        <ul class="text-sm text-n-slate-11 space-y-1 ml-6 list-disc">
          <li>{{ t('CANNED_MGMT.IMPORT.INSTRUCTIONS.REQUIRED') }}</li>
          <li>{{ t('CANNED_MGMT.IMPORT.INSTRUCTIONS.OPTIONAL') }}</li>
          <li>{{ t('CANNED_MGMT.IMPORT.INSTRUCTIONS.TRANSLATIONS') }}</li>
          <li>{{ t('CANNED_MGMT.IMPORT.INSTRUCTIONS.FILE_SIZE') }}</li>
          <li>{{ t('CANNED_MGMT.IMPORT.INSTRUCTIONS.ROWS') }}</li>
        </ul>
        <Button
          slate
          xs
          :label="t('CANNED_MGMT.IMPORT.DOWNLOAD_TEMPLATE')"
          icon="i-lucide-download"
          @click="downloadTemplate"
        />
      </div>

      <!-- Upload Area -->
      <div
        v-if="!hasFile"
        class="border-2 border-dashed rounded-lg p-8 text-center transition-colors"
        :class="{
          'border-n-brand bg-n-brand/5': isDragging,
          'border-n-weak hover:border-n-brand/50': !isDragging,
        }"
        @drop="handleDrop"
        @dragover="handleDragOver"
        @dragleave="handleDragLeave"
      >
        <Icon icon="i-lucide-upload-cloud" class="size-12 mx-auto mb-4 text-n-slate-11" />
        <p class="text-n-slate-12 font-medium mb-2">
          {{ t('CANNED_MGMT.IMPORT.DROPZONE.TITLE') }}
        </p>
        <p class="text-sm text-n-slate-11 mb-4">
          {{ t('CANNED_MGMT.IMPORT.DROPZONE.SUBTITLE') }}
        </p>
        <Button
          slate
          :label="t('CANNED_MGMT.IMPORT.ACTIONS.BROWSE')"
          icon="i-lucide-folder-open"
          @click="openFilePicker"
        />
        <input
          ref="fileInputRef"
          type="file"
          accept=".csv"
          class="hidden"
          @change="handleFileSelect"
        />
      </div>

      <!-- Selected File -->
      <div v-else class="border border-n-weak rounded-lg p-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <Icon icon="i-lucide-file-text" class="size-8 text-n-brand" />
            <div>
              <p class="font-medium text-n-slate-12">{{ selectedFile.name }}</p>
              <p class="text-sm text-n-slate-11">
                {{ (selectedFile.size / 1024).toFixed(2) }} KB
              </p>
            </div>
          </div>
          <Button
            v-if="!isUploading"
            icon="i-lucide-x"
            slate
            xs
            faded
            @click="removeFile"
          />
        </div>
      </div>

      <!-- Results -->
      <div v-if="hasResults" class="space-y-3">
        <div :class="['rounded-lg p-4', summaryClasses.container]">
          <h4 :class="['font-semibold mb-2', summaryClasses.heading]">
            {{ t('CANNED_MGMT.IMPORT.SUMMARY.TITLE') }}
          </h4>
          <div :class="['text-sm space-y-1', summaryClasses.body]">
            <p v-if="summaryMessage" class="font-medium">
              {{ summaryMessage }}
            </p>
            <p v-if="uploadResults.processed">
              {{ t('CANNED_MGMT.IMPORT.SUMMARY.PROCESSED', { count: uploadResults.processed }) }}
            </p>
            <p v-if="uploadResults.success">
              {{ t('CANNED_MGMT.IMPORT.SUMMARY.SUCCESS', { count: uploadResults.success }) }}
            </p>
            <p v-if="uploadResults.created">
              {{ t('CANNED_MGMT.IMPORT.SUMMARY.CREATED', { count: uploadResults.created }) }}
            </p>
            <p v-if="uploadResults.updated">
              {{ t('CANNED_MGMT.IMPORT.SUMMARY.UPDATED', { count: uploadResults.updated }) }}
            </p>
            <p v-if="uploadResults.skipped?.length">
              {{ t('CANNED_MGMT.IMPORT.SUMMARY.SKIPPED', { count: uploadResults.skipped.length }) }}
            </p>
            <p v-if="uploadResults.errors?.length" class="text-n-ruby-11">
              {{ t('CANNED_MGMT.IMPORT.SUMMARY.ERRORS', { count: uploadResults.errors.length }) }}
            </p>
          </div>
        </div>

        <!-- Errors -->
        <div v-if="uploadResults.errors?.length" class="bg-n-ruby-2 border border-n-ruby-6 rounded-lg p-4">
          <h4 class="font-semibold text-n-ruby-11 mb-2">
            {{ t('CANNED_MGMT.IMPORT.SUMMARY.ERRORS_TITLE') }}
          </h4>
          <div class="max-h-40 overflow-y-auto space-y-1">
            <p
              v-for="(error, index) in uploadResults.errors"
              :key="index"
              class="text-sm text-n-ruby-11"
            >
              {{ t('CANNED_MGMT.IMPORT.SUMMARY.ERROR_ROW', { row: error.row, message: error.message }) }}
            </p>
          </div>
        </div>

        <!-- Skipped -->
        <div v-if="uploadResults.skipped?.length" class="bg-n-amber-2 border border-n-amber-6 rounded-lg p-4">
          <h4 class="font-semibold text-n-amber-11 mb-2">
            {{ t('CANNED_MGMT.IMPORT.SUMMARY.SKIPPED_TITLE') }}
          </h4>
          <div class="max-h-40 overflow-y-auto space-y-1">
            <p
              v-for="(skipped, index) in uploadResults.skipped"
              :key="index"
              class="text-sm text-n-amber-11"
            >
              {{ t('CANNED_MGMT.IMPORT.SUMMARY.SKIPPED_ROW', { row: skipped.row, reason: skipped.reason }) }}
            </p>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex justify-end gap-2 pt-4 border-t border-n-weak">
        <Button
          slate
          :label="t('CANNED_MGMT.IMPORT.ACTIONS.CANCEL')"
          :disabled="isUploading"
          @click="closeModal"
        />
        <Button
          v-if="!hasResults"
          :label="t('CANNED_MGMT.IMPORT.ACTIONS.IMPORT')"
          icon="i-lucide-upload"
          :disabled="!hasFile"
          :is-loading="isUploading"
          @click="handleImport"
        />
        <Button
          v-else
          :label="t('CANNED_MGMT.IMPORT.ACTIONS.DONE')"
          @click="closeModal"
        />
      </div>
    </div>
  </div>
</template>
